import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { PopulatedDatabaseQuestion, SafeDatabaseUser } from '../../../types/types';
import { reportUser } from '../../../services/userService';
import useToast from '../../../hooks/useToast';

interface ReportPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: PopulatedDatabaseQuestion;
  currentUser: SafeDatabaseUser;
}

/**
 * Report Post Modal Component
 *
 * Provides a dialog for reporting a post for inappropriate content.
 * Users can enter a reason for the report and submit it for admin review.
 */
const ReportPostModal: React.FC<ReportPostModalProps> = ({
  isOpen,
  onClose,
  post,
  currentUser,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a reason for the report',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // We're reporting the user who posted this content
      const response = await reportUser(
        currentUser.username,
        post.askedBy,
        `Reported for post "${post.title}": ${reason}`,
      );

      if (response.error) {
        throw new Error(response.error as string);
      }

      toast({
        title: 'Report Submitted',
        description: 'Your report has been submitted and will be reviewed by administrators.',
      });

      setReason('');
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to submit report: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Report Post</DialogTitle>
          <DialogDescription>
            Report &quot;{post.title}&quot; by {post.askedBy} for inappropriate content. Your report
            will be reviewed by administrators.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='space-y-2'>
            <label htmlFor='reason' className='text-sm font-medium'>
              Reason for Report
            </label>
            <Textarea
              id='reason'
              placeholder='Please explain why you are reporting this post'
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={5}
              className='resize-none'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPostModal;
